/**
 * WebSocket Service
 * Real-time communication service using STOMP over SockJS
 */

import { Client, ReconnectionTimeMode } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { WEBSOCKET } from '../constants';
import { isAccessTokenExpired, deleteAuthData, getAccessToken } from '../auth/authUtils';

class WebSocketService {
  constructor() {
    this.client = null;
    this.connected = false;
    this.subscriptions = new Map();
    this.messageListeners = new Map();
    this.connectionListeners = [];
  }

  /**
   * Connect to WebSocket server
   * @param {string} token - Authentication token
   * @returns {Promise}
   */
  connect(token) {
    // Already connecting or connected — StompJS owns the lifecycle from here.
    if (this.client && this.client.active) {
      return Promise.resolve(this.client);
    }

    // Don't even open a socket with an expired token.
    if (isAccessTokenExpired()) {
      console.warn('Token expired, redirecting to login');
      this.handleExpiredToken();
      return Promise.reject(new Error('Token expired'));
    }

    return new Promise((resolve, reject) => {
      // Resolve/reject only the *first* connection attempt; later automatic
      // reconnects must not settle this promise again.
      let settled = false;

      this.client = new Client({
        // Read the freshest token on every (re)connect so a token refreshed by the
        // HTTP layer is used, instead of reusing the one captured at first connect.
        webSocketFactory: () =>
          new SockJS(`${process.env.REACT_APP_URL}ws?token=${getAccessToken()}`),

        debug: (str) => {
          if (process.env.NODE_ENV === 'development') {
            console.log('[WebSocket DEBUG]:', str);
          }
        },

        // Single, well-defined reconnect strategy (StompJS built-in). Exponential
        // backoff with a ceiling avoids hammering flaky mobile networks, and there is
        // no hard attempt cap so the connection recovers on its own when signal returns.
        reconnectDelay: WEBSOCKET.RECONNECT_DELAY,
        reconnectTimeMode: ReconnectionTimeMode.EXPONENTIAL,
        maxReconnectDelay: WEBSOCKET.MAX_RECONNECT_DELAY,

        // Abort a handshake that stalls on a slow link and let the reconnect logic
        // retry, rather than hanging indefinitely on a half-open connection.
        connectionTimeout: WEBSOCKET.CONNECTION_TIMEOUT,

        // Gentle heartbeats sized for high-latency mobile connections.
        heartbeatIncoming: WEBSOCKET.HEARTBEAT_INCOMING,
        heartbeatOutgoing: WEBSOCKET.HEARTBEAT_OUTGOING,
        heartbeatToleranceMultiplier: WEBSOCKET.HEARTBEAT_TOLERANCE,

        // Discard a socket that already failed a heartbeat instead of waiting for the
        // OS to time out a half-open TCP connection (common when a mobile radio drops).
        discardWebsocketOnCommFailure: true,

        // Runs before the first connect and before every reconnect: the single place
        // that enforces token expiry across the whole connection lifecycle.
        beforeConnect: () => {
          if (isAccessTokenExpired()) {
            console.warn('Token expired, stopping WebSocket and redirecting to login');
            this.handleExpiredToken();
          }
        },

        onConnect: (frame) => {
          console.log('✅ Connected to WebSocket');
          this.connected = true;
          this.subscribeToOnlineUsers();
          this.notifyConnection(true);
          if (!settled) {
            settled = true;
            resolve(frame);
          }
        },

        onStompError: (frame) => {
          console.error('❌ STOMP error:', frame);
          this.connected = false;
          this.notifyConnection(false);

          // If the broker rejected us over auth, stop and send the user to login.
          const errorMessage = frame.headers?.message || frame.body || '';
          if (
            errorMessage.includes('expired') ||
            errorMessage.includes('401') ||
            errorMessage.includes('Unauthorized') ||
            isAccessTokenExpired()
          ) {
            console.warn('Token rejected by server, redirecting to login');
            this.handleExpiredToken();
          }

          if (!settled) {
            settled = true;
            reject(new Error('WebSocket connection failed'));
          }
        },

        // Fired on every socket close, clean or abrupt. StompJS schedules the
        // reconnect itself; here we only reflect state. Expiry is handled in
        // beforeConnect so reconnects with a dead token are stopped there.
        onWebSocketClose: () => {
          console.log('⚠️ WebSocket connection closed');
          this.connected = false;
          this.notifyConnection(false);
        },

        onWebSocketError: (evt) => {
          console.warn('WebSocket transport error:', evt?.message || evt);
        },
      });

      this.client.activate();
    });
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect() {
    if (!this.client) return;

    this.subscriptions.forEach((sub) => {
      if (Array.isArray(sub)) {
        sub.forEach((s) => s.unsubscribe?.());
      } else {
        sub?.unsubscribe?.();
      }
    });
    this.subscriptions.clear();

    // deactivate() also cancels any pending automatic reconnect.
    this.client.deactivate();
    this.client = null;
    this.connected = false;
    this.notifyConnection(false);
    console.log('🔌 WebSocket disconnected');
  }

  /**
   * Handle expired token - disconnect and redirect to login
   */
  handleExpiredToken() {
    this.disconnect();
    deleteAuthData();
    window.location.href = '/login';
  }

  // ========= SUBSCRIPTIONS =========

  /**
   * Subscribe to online users updates
   */
  subscribeToOnlineUsers() {
    if (!this.connected) return;

    const handleUpdate = (message) => {
      this.safeParse(message.body, (onlineUsers) => {
        if (this.messageListeners.has('onlineUsers')) {
          this.messageListeners.get('onlineUsers').forEach((cb) => cb(onlineUsers));
        }
      });
    };

    const publicSub = this.client.subscribe('/topic/online', handleUpdate);
    const privateSub = this.client.subscribe('/user/queue/online', handleUpdate);

    this.subscriptions.set('onlineUsersPublic', publicSub);
    this.subscriptions.set('onlineUsersPrivate', privateSub);
  }

  /**
   * Subscribe to user's conversation updates
   * @param {string} username - Username to subscribe for
   * @param {Function} callback - Callback for received events
   * @returns {Function} Unsubscribe function
   */
  subscribeToUserConversations(username, callback) {
    if (!this.connected || !this.client) {
      console.warn('⚠️ WebSocket not connected, cannot subscribe to user feed');
      return null;
    }

    const destination = `/topic/user.${username}.conversations`;

    if (this.subscriptions.has(destination)) {
      this.subscriptions.get(destination).unsubscribe();
      this.subscriptions.delete(destination);
    }

    const sub = this.client.subscribe(destination, (message) => {
      this.safeParse(message.body, (event) => callback(event));
    });

    this.subscriptions.set(destination, sub);

    return () => {
      try {
        sub.unsubscribe();
      } catch (e) { /* ignore */ }
      this.subscriptions.delete(destination);
    };
  }

  // ========= SENDERS =========

  /**
   * Send a message to a conversation
   */
  sendMessage(conversationId, content, tempId, messageType = 'TEXT', replyToId = null) {
    this.safePublish('/app/conversation.sendMessage', {
      conversationId,
      tempId,
      content,
      messageType,
      replyToId
    });
  }

  /**
   * Update a message in a conversation
   */
  updateMessage(conversationId, messageId, content) {
    this.safePublish('/app/conversation.updateMessage', {
      conversationId,
      messageId,
      content
    });
  }

  /**
   * Delete a message in a conversation
   */
  deleteMessage(conversationId, messageId) {
    this.safePublish('/app/conversation.deleteMessage', {
      conversationId,
      messageId
    });
  }

  /**
   * Clear all messages in a conversation
   */
  clearChat(conversationId) {
    this.safePublish('/app/conversation.clearChat', {
      conversationId
    });
  }

  /**
   * Delete a conversation (clears messages and removes from list)
   */
  deleteChat(conversationId) {
    this.safePublish('/app/conversation.deleteChat', {
      conversationId
    });
  }

  /**
   * Send typing started event
   */
  sendTypingStarted(conversationId) {
    this.safePublish('/app/conversation.typing.started', { conversationId });
  }

  /**
   * Send typing stopped event
   */
  sendTypingStopped(conversationId) {
    this.safePublish('/app/conversation.typing.stopped', { conversationId });
  }

  /**
   * Send voice-recording started event (mirrors typing.started)
   */
  sendVoiceRecordingStarted(conversationId) {
    this.safePublish('/app/conversation.voice-recording.started', { conversationId });
  }

  /**
   * Send voice-recording stopped event
   */
  sendVoiceRecordingStopped(conversationId) {
    this.safePublish('/app/conversation.voice-recording.stopped', { conversationId });
  }

  /**
   * Mark messages as read
   */
  markAsRead(conversationId, messageId) {
    if (this.client?.connected) {
      this.client.publish({
        destination: '/app/conversation.markAsRead',
        body: JSON.stringify({
          conversationId: conversationId,
          selfLastReadMessageId: messageId,
        }),
      });
    } else {
      console.warn('❌ WebSocket not connected, cannot send markAsRead');
    }
  }

  // ========= CONNECTION STATE =========

  /**
   * Check if connected
   * @returns {boolean}
   */
  isConnected() {
    return this.connected;
  }

  /**
   * Add message listener
   * @param {string} type - Event type
   * @param {Function} callback - Callback function
   */
  addMessageListener(type, callback) {
    if (!this.messageListeners.has(type)) {
      this.messageListeners.set(type, []);
    }
    const listeners = this.messageListeners.get(type);

    if (!listeners.includes(callback)) {
      listeners.push(callback);
    }
  }

  /**
   * Remove message listener
   * @param {string} type - Event type
   * @param {Function} callback - Callback function
   */
  removeMessageListener(type, callback) {
    if (!this.messageListeners.has(type)) return;
    const listeners = this.messageListeners.get(type).filter((cb) => cb !== callback);
    if (listeners.length) {
      this.messageListeners.set(type, listeners);
    } else {
      this.messageListeners.delete(type);
    }
  }

  /**
   * Add connection state listener
   * @param {Function} callback - Callback function
   */
  addConnectionListener(callback) {
    this.connectionListeners.push(callback);
  }

  /**
   * Remove connection state listener
   * @param {Function} callback - Callback function
   */
  removeConnectionListener(callback) {
    this.connectionListeners = this.connectionListeners.filter((cb) => cb !== callback);
  }

  // ========= HELPERS =========

  /**
   * Notify all connection-state listeners, isolating a throwing listener so it
   * can't break the others.
   */
  notifyConnection(state) {
    this.connectionListeners.forEach((cb) => {
      try {
        cb(state);
      } catch (e) {
        console.warn('Connection listener threw:', e);
      }
    });
  }

  /**
   * Safely parse JSON message
   */
  safeParse(body, callback) {
    try {
      const data = JSON.parse(body);
      callback(data);
    } catch (err) {
      console.warn('Failed to parse message body:', body, err);
    }
  }

  /**
   * Safely publish message
   */
  safePublish(destination, body) {
    if (!this.connected) {
      console.warn('⚠️ WebSocket not connected, cannot publish to', destination);
      return;
    }
    this.client.publish({ destination, body: JSON.stringify(body) });
  }
}

const webSocketService = new WebSocketService();
export default webSocketService;

